import { Descriptions, Space, Tag, Typography } from 'antd'
import React, { FunctionComponent } from 'react'

import {
  LeadState,
  leadStateColors,
  leadStateLabels,
} from '../../../common/businessContactTypes'
import { BusinessContactTableItem } from '../../utils/useBusinessContactData'
import { Min } from '../DateFormat/DateFormat'

export interface BusinessContactTableExpandedProps {
  record: BusinessContactTableItem
}

const normalizeLeadState = (value: unknown): LeadState | undefined => {
  if (typeof value !== 'string') return undefined
  const lowered = value.toLowerCase()
  return (Object.values(LeadState) as string[]).includes(lowered)
    ? (lowered as LeadState)
    : undefined
}

const toTelHref = (phone: string): string => {
  const normalized = phone.replace(/[^\d+]/g, '')
  return `tel:${normalized}`
}

const googleMapsLinkFromGps = (lat: number, lon: number): string => {
  const query = `${lat},${lon}`
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`
}

const formatUrlForDisplay = (value: string): string => {
  return value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/g, '')
}

export const BusinessContactTableExpanded: FunctionComponent<BusinessContactTableExpandedProps> =
  ({ record }) => {
    const normalizedState = normalizeLeadState(record.state)

    return (
      <div className="p-4 bg-neutral-50">
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 2, md: 3 }}
          bordered
          className="bg-white"
        >
          <Descriptions.Item label="Název" span={3}>
            {record.title || record.ogTitle || '---'}
          </Descriptions.Item>
          <Descriptions.Item label="Popis" span={3}>
            <Typography.Paragraph
              ellipsis={{ rows: 3, expandable: true }}
              className="!mb-0"
            >
              {record.description || '---'}
            </Typography.Paragraph>
          </Descriptions.Item>
          <Descriptions.Item label="Kategorie" span={3}>
            {record.categories?.length ? (
              <Space wrap>
                {record.categories.map((cat, idx) => (
                  <Tag key={idx}>{cat}</Tag>
                ))}
              </Space>
            ) : (
              '---'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="E-maily">
            {record.emails?.length ? (
              <Space wrap>
                {record.emails.map((email, idx) => (
                  <a key={`${email}-${idx}`} href={`mailto:${email}`}>
                    {email}
                  </a>
                ))}
              </Space>
            ) : (
              '---'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Telefony">
            {record.phoneNumbers?.length ? (
              <Space wrap>
                {record.phoneNumbers.map((phone, idx) => (
                  <a key={`${phone}-${idx}`} href={toTelHref(phone)}>
                    {phone}
                  </a>
                ))}
              </Space>
            ) : (
              '---'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Weby">
            {record.websites?.length ? (
              <Space direction="vertical" size={0}>
                {record.websites.map((web, idx) => (
                  <a
                    key={idx}
                    href={web}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {formatUrlForDisplay(web)}
                  </a>
                ))}
              </Space>
            ) : (
              '---'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Ulice">
            {record.streetAddress || '---'}
          </Descriptions.Item>
          <Descriptions.Item label="Město">
            {record.locality || '---'}
          </Descriptions.Item>
          <Descriptions.Item label="PSČ">
            {record.postalCode || '---'}
          </Descriptions.Item>
          <Descriptions.Item label="Země">
            {record.countryName || '---'}
          </Descriptions.Item>
          <Descriptions.Item label="Stav">
            {normalizedState ? (
              <Tag color={leadStateColors[normalizedState]}>
                {leadStateLabels[normalizedState]}
              </Tag>
            ) : (
              <Tag color="default">
                {typeof record.state === 'string' ? record.state : '---'}
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Mapy">
            {record.latitude != null && record.longitude != null ? (
              <a
                href={googleMapsLinkFromGps(record.latitude, record.longitude)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Otevřít mapy ({record.latitude}, {record.longitude})
              </a>
            ) : (
              '---'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Aktualizováno">
            <Min>{record.updatedAt}</Min>
          </Descriptions.Item>
          <Descriptions.Item label="Datum zdroje (Lastmod)">
            {record.lastmod || '---'}
          </Descriptions.Item>
        </Descriptions>
      </div>
    )
  }
